/**
 * 전역 confirm/notify 다이얼로그 — `window.confirm` / `alert` 대체.
 *
 * <h3>왜 만들었나</h3>
 * <p>브라우저 native {@code window.confirm} 은 OS 별로 모양이 다르고 페이지 URL 이 헤더에 노출됨
 * (스크린샷의 "localhost:3000 내용:" 등). 디자인 시스템과 분리되어 톤이 깨져 보임.
 * 사용자 요청 (2026-05-05) 으로 자체 모달로 통일.
 *
 * <h3>사용법 — Promise 기반 imperative API</h3>
 * <pre>
 *   import { confirm } from "../../util/confirmDialog";
 *
 *   const ok = await confirm({
 *       title:        "정말 삭제할까요?",
 *       message:      "이 메일을 휴지통으로 이동합니다.",
 *       confirmLabel: "삭제",
 *       cancelLabel:  "취소",
 *       variant:      "danger",   // 기본 "default"
 *   });
 *   if (!ok) return;
 *   // 진행
 * </pre>
 *
 * <h3>동작 원리</h3>
 * <p>{@link ConfirmDialogHost} 가 앱 루트에 한 번만 마운트. 외부에서 {@link confirm} 호출 시
 * 내부 emitter 가 host 의 state 를 갱신해 다이얼로그를 표시. 사용자 클릭 결과를 Promise 로 resolve.
 *
 * <h3>여러 confirm 동시 호출</h3>
 * <p>큐 방식 — 첫 호출 결과 resolve 될 때까지 다음 호출은 대기. UX 단순함 우선.
 */
import React, { useEffect, useState } from "react";
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography,
} from "@mui/material";
import { ds } from "../styles/designSystem";
import ActionButton from "../component/common/ActionButton";

/* ── 외부 → host 통신 emitter (singleton) ─────────────────────────── */
let _resolveCurrent = null;       // 현재 진행 중 confirm 의 resolver
let _setHostState = null;         // host 의 setState — host 마운트 시 등록
const _queue = [];                // 동시 호출 대기 큐

/**
 * 외부 사용 진입점 — Promise<boolean> 반환.
 *
 * @param {object} opts
 * @param {string} [opts.title]         제목 (없으면 message 만 표시)
 * @param {string} opts.message         본문 메시지 (필수). 줄바꿈 \n 허용.
 * @param {string} [opts.confirmLabel]  확인 버튼 라벨 (기본 "확인")
 * @param {string} [opts.cancelLabel]   취소 버튼 라벨 (기본 "취소")
 * @param {"default"|"danger"} [opts.variant]  "danger" 면 확인 버튼 빨강
 * @returns {Promise<boolean>} 확인=true, 취소/X=false
 */
export function confirm(opts) {
    return new Promise((resolve) => {
        const item = { opts: { variant: "default", ...opts }, resolve };
        if (_resolveCurrent) {
            // 이미 다이얼로그 진행 중 — 큐에 enqueue
            _queue.push(item);
            return;
        }
        _showItem(item);
    });
}

/**
 * 진행 중인 confirm 을 <b>사용자 대신 취소</b>로 닫는다(대기 큐도 비운다).
 *
 * <p>물어본 이유 자체가 사라졌을 때 쓴다 — 예를 들어 "세션을 연장할까요?" 를 띄워 둔 채
 * 세션이 그냥 만료돼 로그인 화면으로 넘어가면, 답할 대상이 없는 모달만 위에 떠 있게 된다.
 * 사용자가 아직 안 눌렀을 뿐이므로 결과는 취소(false)로 본다.
 */
export function dismissConfirm() {
    _queue.length = 0;
    _resolveCurrent?.(false);
}

function _showItem(item) {
    _resolveCurrent = (result) => {
        item.resolve(result);
        _resolveCurrent = null;
        // 큐 다음 처리
        const next = _queue.shift();
        if (next) {
            // 다이얼로그 닫힘 애니메이션 후 다음 표시
            setTimeout(() => _showItem(next), 80);
        } else {
            _setHostState?.({ open: false });
        }
    };
    _setHostState?.({ open: true, opts: item.opts });
}

/* ─────────────────────────────────────────────────────────────────────
 * confirmNotify() — alert 대체. confirm() 과 같은 imperative API, 확인 버튼 하나만.
 * 사용처에서 import { confirmNotify } from "../../util/confirmDialog" 한 줄만 추가하면 됨.
 * NotifyDialogHost 는 confirm 의 host 와 함께 index.js 에 1회 마운트.
 *
 * ⚠ util/toast.js 의 notify() 와 혼동 금지 — 이름이 같아 화면마다 토스트/모달이
 *   갈라지는 문제가 있어 이쪽을 confirmNotify 로 개명했다(2026-07-23).
 *   · notify(msg, type)        → 상단 토스트. 응답 없음. 단순 알림은 전부 이쪽.
 *   · confirmNotify({message}) → 모달. 사용자가 "확인" 눌러야 Promise resolve.
 *     흐름을 반드시 멈춰야 할 때(로그아웃 직전 안내 등)만 쓴다.
 * ──────────────────────────────────────────────────────────────────── */
let _resolveNotify = null;
let _setNotifyState = null;
const _notifyQueue = [];

/**
 * 전역 알림 모달 — 사용자가 "확인" 을 눌러야 Promise 가 resolve 된다.
 * 단순 알림은 util/toast.js 의 notify() 를 쓰고, 여기는 응답을 받아야 할 때만.
 *
 * @param {object} opts
 * @param {string} [opts.title]            제목
 * @param {string} opts.message            본문 (필수)
 * @param {string} [opts.confirmLabel]     확인 버튼 라벨 (기본 "확인")
 * @param {"info"|"success"|"warning"|"error"} [opts.severity]  색상 톤
 * @returns {Promise<void>} 확인 클릭 시 resolve
 */
export function confirmNotify(opts) {
    return new Promise((resolve) => {
        const item = { opts: { severity: "info", ...opts }, resolve };
        if (_resolveNotify) { _notifyQueue.push(item); return; }
        _showNotifyItem(item);
    });
}

function _showNotifyItem(item) {
    _resolveNotify = () => {
        item.resolve();
        _resolveNotify = null;
        const next = _notifyQueue.shift();
        if (next) setTimeout(() => _showNotifyItem(next), 80);
        else _setNotifyState?.({ open: false });
    };
    _setNotifyState?.({ open: true, opts: item.opts });
}

const severityColor = (s) => {
    switch (s) {
        case "error":   return "#dc2626";
        case "warning": return "#ca8a04";
        case "success": return "#16a34a";
        default:        return "#0f172a";
    }
};

export function NotifyDialogHost() {
    const [{ open, opts }, setState] = useState({ open: false, opts: {} });
    useEffect(() => {
        _setNotifyState = setState;
        return () => { _setNotifyState = null; _resolveNotify = null; };
    }, []);
    const handleOk = () => _resolveNotify?.();
    return (
        <Dialog
            open={open}
            onClose={handleOk}
            PaperProps={{
                sx: {
                    borderRadius: "12px", minWidth: 360, maxWidth: 480,
                    boxShadow: "0 24px 48px rgba(15, 23, 42, 0.18)",
                },
            }}
            transitionDuration={120}
        >
            {opts?.title && (
                <DialogTitle sx={{
                    fontSize: 15, fontWeight: 800,
                    color: severityColor(opts.severity),
                    pb: 1, pt: 2.5, px: 3,
                }}>
                    {opts.title}
                </DialogTitle>
            )}
            <DialogContent sx={{ px: 3, pt: opts?.title ? 0 : 3, pb: 1 }}>
                <Typography sx={{
                    fontSize: 13.5, color: "#334155", lineHeight: 1.6,
                    whiteSpace: "pre-line",
                }}>
                    {opts?.message || ""}
                </Typography>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
                <ActionButton
                    variant="confirm"
                    onClick={handleOk}
                    autoFocus
                    startIcon={false}
                    label={opts?.confirmLabel || "확인"}
                    sx={{ minWidth: 80 }}
                />
            </DialogActions>
        </Dialog>
    );
}

/* ── 호스트 컴포넌트 — 앱 루트에 1번만 마운트 ────────────────────── */
export function ConfirmDialogHost() {
    const [{ open, opts }, setState] = useState({ open: false, opts: {} });

    useEffect(() => {
        _setHostState = setState;
        return () => {
            _setHostState = null;
            _resolveCurrent = null;
        };
    }, []);

    const handleConfirm = () => _resolveCurrent?.(true);
    const handleCancel  = () => _resolveCurrent?.(false);

    const isDanger = opts?.variant === "danger";
    const title    = opts?.title;
    const message  = opts?.message || "";
    const confirmLabel = opts?.confirmLabel || "확인";
    const cancelLabel  = opts?.cancelLabel  || "취소";

    return (
        <Dialog
            open={open}
            onClose={handleCancel}
            // 다이얼로그 등장은 커서 위치와 상관없이 항상 화면 중앙 — 안정적 UX.
            // ESC / 바깥 클릭 시 cancel 처리 (handleCancel 가 false resolve).
            PaperProps={{
                sx: {
                    borderRadius: "12px",
                    minWidth: 360,
                    maxWidth: 480,
                    boxShadow: "0 24px 48px rgba(15, 23, 42, 0.18)",
                },
            }}
            transitionDuration={120}
        >
            {title && (
                <DialogTitle sx={{
                    fontSize: 15, fontWeight: 700, color: "#0f172a",
                    pb: 1, pt: 2.5, px: 3,
                }}>
                    {title}
                </DialogTitle>
            )}

            <DialogContent sx={{ px: 3, pt: title ? 0 : 3, pb: 1 }}>
                <Typography sx={{
                    fontSize: 13.5, color: "#334155", lineHeight: 1.6,
                    whiteSpace: "pre-line",  // \n 줄바꿈 보존
                }}>
                    {message}
                </Typography>
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 2.5, pt: 1, gap: 1 }}>
                <ActionButton
                    variant="cancel"
                    onClick={handleCancel}
                    label={cancelLabel}
                    sx={{ minWidth: 80 }}
                />
                <ActionButton
                    variant="confirm"
                    onClick={handleConfirm}
                    autoFocus
                    startIcon={false}
                    label={confirmLabel}
                    sx={{
                        minWidth: 80,
                        ...(isDanger && {
                            bgcolor: "#dc2626",
                            "&:hover": { bgcolor: "#b91c1c" },
                        }),
                    }}
                />
            </DialogActions>
        </Dialog>
    );
}
